import { uniq } from "es-toolkit/array";
import {
  isNumericMetricField,
  resolveMetricAggregationForField,
  resolveRatioDependencyForDataset,
} from "@/lib/ai/visualization/data/adapters/metric-aggregation";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import type { ValidateVizSpecArgs } from "@/lib/ai/visualization/views/shared/view-definition";

function findField(
  dataset: VizDataset,
  fieldName: string | null | undefined,
): VizDataset["fields"][number] | null {
  if (typeof fieldName !== "string" || fieldName.length === 0) return null;
  return dataset.fields.find((field) => field.name === fieldName) ?? null;
}

function isMetricField(field: VizDataset["fields"][number]): boolean {
  return isNumericMetricField(field);
}

function isDimensionField(field: VizDataset["fields"][number]): boolean {
  return !isMetricField(field);
}

function isTimeDimensionField(field: VizDataset["fields"][number]): boolean {
  return (
    isDimensionField(field) && (field.role === "x" || field.type === "date")
  );
}

function resolveMetricFamily(field: VizDataset["fields"][number]): string {
  return field.metricFamily ?? field.name;
}

function resolveComparisonRole(
  field: VizDataset["fields"][number],
): VizDataset["fields"][number]["comparisonRole"] | null {
  return field.comparisonRole ?? null;
}

function isCategoryDimensionField(
  field: VizDataset["fields"][number],
): boolean {
  // bar 系 view の category axis は date も受けるため、
  // string に限定せず non-metric field 全体を category 候補として扱う。
  return isDimensionField(field);
}

function isAuthoringVisibleField(field: VizDataset["fields"][number]): boolean {
  return field.internal !== true;
}

/**
 * spec validation issue を 1 箇所で正規化する。
 *
 * @remarks
 * compile failure の reason code とは分離し、view-local validator は
 * path/code/message の構造化 payload だけを返す。
 */
export function buildSpecValidationIssue(args: {
  path: string;
  message: string;
  code: string;
  phase?: VizAuthoringIssue["phase"];
  expected?: string;
  actual?: string;
}): VizAuthoringIssue {
  return {
    path: args.path,
    message: args.message,
    code: args.code,
    phase: args.phase ?? "validation",
    ...(args.expected ? { expected: args.expected } : null),
    ...(args.actual ? { actual: args.actual } : null),
  };
}

/**
 * issue 配列を tool-facing の短い診断文へ圧縮する。
 */
export function summarizeSpecValidationIssueList(
  issues: VizAuthoringIssue[],
): string {
  return issues
    .slice(0, 3)
    .map((issue) => `${issue.path}: ${issue.message}`)
    .join("; ");
}

/**
 * dataset requirement 判定用に metric field 数を数える。
 */
export function countMetricFields(dataset: VizDataset): number {
  return dataset.fields.filter(
    (field) => isAuthoringVisibleField(field) && isMetricField(field),
  ).length;
}

/**
 * time-series view が要求する time dimension の有無を返す。
 */
export function hasTimeDimension(dataset: VizDataset): boolean {
  return dataset.fields.some(
    (field) => isAuthoringVisibleField(field) && isTimeDimensionField(field),
  );
}

/**
 * category axis を持つ view が使える dimension の有無を返す。
 */
export function hasCategoryDimension(dataset: VizDataset): boolean {
  return dataset.fields.some(
    (field) =>
      isAuthoringVisibleField(field) && isCategoryDimensionField(field),
  );
}

/**
 * field key の存在と role を検証し、問題があれば issues へ積む。
 *
 * @remarks
 * validator は fail-fast せず複数 issue を返したいため、throw せず push で集約する。
 */
export function validateFieldRole(args: {
  issues: VizAuthoringIssue[];
  dataset: VizDataset;
  path: string;
  fieldName: string | null | undefined;
  role: "metric" | "dimension" | "time-dimension" | "any";
}): void {
  if (typeof args.fieldName !== "string" || args.fieldName.length === 0) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "missing-field",
        message: "field key is required",
      }),
    );
    return;
  }

  const field = findField(args.dataset, args.fieldName);
  if (!field) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "unknown-field",
        message: `field '${args.fieldName}' does not exist`,
        expected: "existing dataset field key",
        actual: args.fieldName,
      }),
    );
    return;
  }

  if (args.role !== "any" && !isAuthoringVisibleField(field)) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "internal-field",
        message: `field '${args.fieldName}' is not available for chart authoring`,
        expected: "authoring-visible dataset field key",
        actual: args.fieldName,
      }),
    );
    return;
  }

  if (args.role === "metric" && !isMetricField(field)) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "invalid-field-role",
        message: `field '${args.fieldName}' must be numeric`,
        expected: "metric field key",
        actual: field.role,
      }),
    );
  }
  if (args.role === "dimension" && !isDimensionField(field)) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "invalid-field-role",
        message: `field '${args.fieldName}' must be a dimension`,
        expected: "dimension field key",
        actual: field.role,
      }),
    );
  }
  if (args.role === "time-dimension" && !isTimeDimensionField(field)) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "invalid-field-role",
        message: `field '${args.fieldName}' must be a time dimension`,
        expected: "authoring-visible time dimension field key",
        actual: `${field.role}:${field.type}`,
      }),
    );
  }
}

function isProjectableDimensionValue(value: unknown): boolean {
  if (typeof value === "string") return value.trim().length > 0;
  return typeof value === "number" && Number.isFinite(value);
}

function isFiniteMetricValue(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Binding の field 契約と row-level aggregation 入力を分けて検証する。
 *
 * @returns
 * `null` は missing/internal/wrong-role field のため、この guard では投影可否を
 * 判定できないことを示す。caller は field validation が専用 issue を返すため
 * `null` を無視してよい。一意cellのratioは直接値または同一rowの依存値で保持できる。
 * `false` は field 契約が有効でも実現済みcellがない、またはduplicate/
 * always-aggregate cellの全selected metricをadditiveかcell-local dependenciesを
 * 持つratioとして集約できないことを示す。
 */
function canProjectMetricBinding(args: {
  dataset: VizDataset;
  categoryKeys: string[];
  metricKeys: string[];
  alwaysAggregate?: boolean;
}): boolean | null {
  const categoryFields = args.categoryKeys.map((key) =>
    findField(args.dataset, key),
  );
  const metricFields = args.metricKeys.map((key) =>
    findField(args.dataset, key),
  );
  if (
    args.categoryKeys.length === 0 ||
    args.metricKeys.length === 0 ||
    categoryFields.some(
      (field) => !field || field.internal === true || isMetricField(field),
    ) ||
    metricFields.some(
      (field) => !field || field.internal === true || !isMetricField(field),
    )
  ) {
    return null;
  }

  const rowsByCell = new Map<string, VizDataset["rows"]>();
  for (const row of args.dataset.rows) {
    const categoryValues = args.categoryKeys.map((key) => row[key]);
    if (!categoryValues.every(isProjectableDimensionValue)) continue;

    const cellKey = JSON.stringify(categoryValues.map(String));
    const cellRows = rowsByCell.get(cellKey) ?? [];
    cellRows.push(row);
    rowsByCell.set(cellKey, cellRows);
  }

  const cells = [...rowsByCell.values()];
  if (cells.length === 0) return false;

  const requiresAggregation =
    args.alwaysAggregate === true ||
    cells.some((cellRows) => cellRows.length > 1);
  return args.metricKeys.every((metricKey, metricIndex) => {
    if (!requiresAggregation) {
      const hasDirectValue = cells.some((cellRows) =>
        cellRows.some((row) => isFiniteMetricValue(row[metricKey])),
      );
      if (hasDirectValue) return true;

      const metricField = metricFields[metricIndex];
      if (!metricField) return false;
      const aggregation = resolveMetricAggregationForField(metricField);
      if (aggregation?.kind !== "ratio") return false;

      const dependency = resolveRatioDependencyForDataset({
        dataset: args.dataset,
        metricKey,
      });
      if (!dependency) return false;

      return cells.some((cellRows) =>
        cellRows.some((row) => {
          const numerator = row[dependency.numeratorKey];
          const denominator = row[dependency.denominatorKey];
          return (
            isFiniteMetricValue(numerator) &&
            isFiniteMetricValue(denominator) &&
            (denominator !== 0 || dependency.zeroDenominatorValue !== null)
          );
        }),
      );
    }

    const metricField = metricFields[metricIndex];
    if (!metricField) return false;
    const aggregation = resolveMetricAggregationForField(metricField);
    if (!aggregation || aggregation.kind === "non-aggregatable") return false;
    if (aggregation.kind === "additive") {
      return cells.some((cellRows) =>
        cellRows.some((row) => isFiniteMetricValue(row[metricKey])),
      );
    }

    const dependency = resolveRatioDependencyForDataset({
      dataset: args.dataset,
      metricKey,
    });
    if (!dependency) return false;

    const cellsWithRatioInput = cells.filter((cellRows) =>
      cellRows.some(
        (row) =>
          isFiniteMetricValue(row[metricKey]) ||
          isFiniteMetricValue(row[dependency.numeratorKey]) ||
          isFiniteMetricValue(row[dependency.denominatorKey]),
      ),
    );
    return cellsWithRatioInput.some((cellRows) => {
      const hasNumerator = cellRows.some((row) =>
        isFiniteMetricValue(row[dependency.numeratorKey]),
      );
      const hasDenominator = cellRows.some((row) =>
        isFiniteMetricValue(row[dependency.denominatorKey]),
      );
      if (!hasNumerator || !hasDenominator) return false;

      const denominatorTotal = cellRows.reduce((total, row) => {
        const value = row[dependency.denominatorKey];
        return isFiniteMetricValue(value) ? total + value : total;
      }, 0);
      return denominatorTotal !== 0 || dependency.zeroDenominatorValue !== null;
    });
  });
}

/**
 * 選択済み dimension/metric の組み合わせが render projection 可能か検証する。
 */
export function pushProjectableMetricBindingIssue(args: {
  issues: VizAuthoringIssue[];
  dataset: VizDataset;
  path: string;
  categoryKeys: string[];
  metricKeys: string[];
  alwaysAggregate?: boolean;
}): void {
  const projectable = canProjectMetricBinding({
    dataset: args.dataset,
    categoryKeys: args.categoryKeys,
    metricKeys: uniq(args.metricKeys),
    alwaysAggregate: args.alwaysAggregate,
  });
  if (projectable !== false) return;

  args.issues.push(
    buildSpecValidationIssue({
      path: args.path,
      code: "unprojectable-binding",
      message:
        "selected dimension cells cannot project every selected metric with valid aggregation inputs",
      expected: "projectable dimension and metric binding",
      actual: args.categoryKeys.join(", "),
    }),
  );
}

/**
 * 選択キー配列の重複を 1 件だけ報告する。
 *
 * @remarks
 * 同じ原因の issue を大量に増やすと retry prompt が読みにくくなるため、
 * 最初の重複だけを返して診断密度を保つ。
 */
export function pushDuplicateValueIssues(args: {
  issues: VizAuthoringIssue[];
  path: string;
  values: string[];
}): void {
  if (args.values.length === 0) return;

  const seen = new Set<string>();
  for (const value of args.values) {
    if (seen.has(value)) {
      args.issues.push(
        buildSpecValidationIssue({
          path: args.path,
          code: "duplicate-value",
          message: `duplicate value '${value}' is not allowed`,
          expected: "unique values",
          actual: value,
        }),
      );
      return;
    }
    seen.add(value);
  }
}

/**
 * 選択済みキー配列が allowedKeys に収まるかを検証する。
 */
export function pushSelectedKeyIssues(args: {
  issues: VizAuthoringIssue[];
  path: string;
  selectedKeys: string[];
  allowedKeys: Set<string>;
  emptyAllowedMessage: string;
}): void {
  if (args.selectedKeys.length === 0) return;
  if (args.allowedKeys.size === 0) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "no-selectable-keys",
        message: args.emptyAllowedMessage,
      }),
    );
    return;
  }

  for (const [index, key] of args.selectedKeys.entries()) {
    if (!args.allowedKeys.has(key)) {
      args.issues.push(
        buildSpecValidationIssue({
          path: `${args.path}[${index}]`,
          code: "invalid-selected-key",
          message: `value '${key}' is not available for this spec`,
          expected: Array.from(args.allowedKeys).join(", "),
          actual: key,
        }),
      );
    }
  }
}

/**
 * label map や sizing map のような keyed object が selected keys と整合するかを検証する。
 */
export function pushMappedKeyIssues(args: {
  issues: VizAuthoringIssue[];
  path: string;
  entries: Record<string, unknown> | null | undefined;
  allowedKeys: Set<string>;
  emptyAllowedMessage: string;
}): void {
  const entries = args.entries ?? {};
  const keys = Object.keys(entries);
  if (keys.length === 0) return;
  if (args.allowedKeys.size === 0) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "no-selectable-keys",
        message: args.emptyAllowedMessage,
      }),
    );
    return;
  }

  for (const key of keys) {
    if (!args.allowedKeys.has(key)) {
      args.issues.push(
        buildSpecValidationIssue({
          path: `${args.path}.${key}`,
          code: "invalid-selected-key",
          message: `value '${key}' is not available for this spec`,
          expected: Array.from(args.allowedKeys).join(", "),
          actual: key,
        }),
      );
    }
  }
}

function resolveRequestedMovingAverageWindows(dataset: VizDataset): number[] {
  const movingAverage = dataset.context?.queryContext?.movingAverage;
  switch (movingAverage) {
    case "7d":
      return [7];
    case "28d":
      return [28];
    case "both":
      return [7, 28];
    default:
      return [];
  }
}

/**
 * queryContext で要求された移動平均 series が compiled spec から落ちていないか検証する。
 *
 * @remarks
 * AI authoring は instruction prose だけだと smoothing を落としやすいため、
 * dataset metadata と tool input (`queryContext.movingAverage`) を突き合わせて
 * repair 可能な binding issue として返す。
 */
function pushRequestedMovingAverageIssues(args: {
  issues: VizAuthoringIssue[];
  dataset: VizDataset;
  path: string;
  selectedSeriesKeys: readonly string[];
}): void {
  const requestedWindows = resolveRequestedMovingAverageWindows(args.dataset);
  if (requestedWindows.length === 0 || args.selectedSeriesKeys.length === 0) {
    return;
  }

  const fieldByName = new Map(
    args.dataset.fields.map((field) => [field.name, field] as const),
  );
  const selectedSeriesKeySet = new Set(args.selectedSeriesKeys);
  const requestedFamilies = new Set<string>();

  for (const selectedSeriesKey of args.selectedSeriesKeys) {
    const field = fieldByName.get(selectedSeriesKey);
    if (!field || !isMetricField(field)) continue;
    requestedFamilies.add(
      `${resolveMetricFamily(field)}::${resolveComparisonRole(field) ?? "base"}`,
    );
  }

  if (requestedFamilies.size === 0) {
    return;
  }

  const missingSeriesKeys = args.dataset.fields.flatMap((field) => {
    if (!isMetricField(field)) return [];
    if (
      typeof field.movingAverageWindowDays !== "number" ||
      !requestedWindows.includes(field.movingAverageWindowDays)
    ) {
      return [];
    }

    const familyKey = `${resolveMetricFamily(field)}::${
      resolveComparisonRole(field) ?? "base"
    }`;
    if (!requestedFamilies.has(familyKey)) {
      return [];
    }

    return selectedSeriesKeySet.has(field.name) ? [] : [field.name];
  });

  for (const missingSeriesKey of missingSeriesKeys) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "missing-requested-moving-average",
        message: `series '${missingSeriesKey}' is required because dataset queryContext requests moving averages for the selected metric family`,
        actual: missingSeriesKey,
      }),
    );
  }
}

/**
 * text prop が空のまま compile されることを防ぐ。
 *
 * @remarks
 * chart label は authoring model が `task.outputLanguage` で返す契約なので、
 * 空文字や null を compiler default へ逃がさず validation で止める。
 */
export function pushRequiredTextIssue(args: {
  issues: VizAuthoringIssue[];
  path: string;
  value: string | null | undefined;
  message: string;
  code?: string;
}): void {
  if (typeof args.value === "string" && args.value.trim().length > 0) {
    return;
  }

  args.issues.push(
    buildSpecValidationIssue({
      path: args.path,
      code: args.code ?? "missing-text",
      message: args.message,
    }),
  );
}

/**
 * keyed label map が required keys を漏れなく含むかを検証する。
 *
 * @remarks
 * unknown key の混入確認は `pushMappedKeyIssues` が担当し、
 * ここでは「必要な label が一つでも欠けていないか」だけを fail-fast する。
 */
export function pushRequiredMappedKeyCoverageIssues(args: {
  issues: VizAuthoringIssue[];
  path: string;
  entries: Record<string, unknown> | null | undefined;
  requiredKeys: readonly string[];
  emptyMessage: string;
}): void {
  const requiredKeys = uniq(
    args.requiredKeys.filter(
      (key): key is string => typeof key === "string" && key.length > 0,
    ),
  );
  if (requiredKeys.length === 0) {
    return;
  }

  const entries = args.entries ?? {};
  if (Object.keys(entries).length === 0) {
    args.issues.push(
      buildSpecValidationIssue({
        path: args.path,
        code: "missing-mapped-entries",
        message: args.emptyMessage,
      }),
    );
    return;
  }

  for (const key of requiredKeys) {
    if (key in entries) {
      continue;
    }

    args.issues.push(
      buildSpecValidationIssue({
        path: `${args.path}.${key}`,
        code: "missing-mapped-key",
        message: `must include a label for '${key}'`,
        expected: requiredKeys.join(", "),
        actual: key,
      }),
    );
  }
}

/**
 * series-like dimension が指す列から実在する系列値だけを抜き出す。
 *
 * @remarks
 * scatter の legend label や grouped trend の realized series は、free-form text
 * ではなく dataset rows に実在した系列値だけを許可する。
 */
export function collectSeriesValues(
  dataset: VizDataset,
  fieldName: string,
): string[] {
  return uniq(
    dataset.rows.flatMap((row) => {
      const value = row[fieldName];
      if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed.length > 0 ? [trimmed] : [];
      }
      if (typeof value === "number" && Number.isFinite(value)) {
        return [String(value)];
      }
      return [];
    }),
  );
}

/**
 * line / area の wide time-series family が共有する binding rule を検証する。
 *
 * @remarks
 * grouped time-series は別 view へ切り出し、この validator では wide metric
 * binding だけを扱う。これにより `seriesByKey/valueKey` との混線を防ぐ。
 */
export function validateTimeSeriesLikeSpec(
  args: ValidateVizSpecArgs<
    Extract<VizSpec, { type: "time-series-line" | "time-series-area" }>
  >,
): VizAuthoringIssue[] {
  const issues: VizAuthoringIssue[] = [];
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.xKey",
    fieldName: args.spec.xKey,
    role: "time-dimension",
  });

  if (args.spec.seriesKeys.length === 0) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.seriesKeys",
        code: "missing-series-keys",
        message:
          "must include at least one metric key in spec.seriesKeys for a renderer-ready spec",
      }),
    );
  }
  pushDuplicateValueIssues({
    issues,
    path: "spec.seriesKeys",
    values: args.spec.seriesKeys,
  });
  args.spec.seriesKeys.forEach((seriesKey, index) => {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: `spec.seriesKeys[${index}]`,
      fieldName: seriesKey,
      role: "metric",
    });
  });

  const allowedSeriesKeys = new Set(args.spec.seriesKeys);
  pushRequiredTextIssue({
    issues,
    path: "spec.componentProps.xLabel",
    value: args.spec.componentProps?.xLabel ?? null,
    message:
      "xLabel is required because chart axis labels are owned by the authoring output",
  });
  pushMappedKeyIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    allowedKeys: allowedSeriesKeys,
    emptyAllowedMessage:
      "seriesLabels requires at least one metric key in spec.seriesKeys",
  });
  pushRequiredMappedKeyCoverageIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    requiredKeys: args.spec.seriesKeys,
    emptyMessage:
      "seriesLabels must include every selected metric key because legend labels are owned by the authoring output",
  });
  pushSelectedKeyIssues({
    issues,
    path: "spec.componentProps.defaultVisibleSeriesKeys",
    selectedKeys: args.spec.componentProps?.defaultVisibleSeriesKeys ?? [],
    allowedKeys: allowedSeriesKeys,
    emptyAllowedMessage:
      "defaultVisibleSeriesKeys requires at least one metric key in spec.seriesKeys",
  });
  pushRequestedMovingAverageIssues({
    issues,
    dataset: args.dataset,
    path: "spec.seriesKeys",
    selectedSeriesKeys: args.spec.seriesKeys,
  });

  return issues;
}
